import { ReactElement, useEffect } from "react";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { useNotifications } from "../common/stateDefs";

export const BackendNotifications = () => {
  const { authUser } = useFirebaseAuthentication();
  const addNotification = useNotifications((state) => state.addNotification);

  useEffect(() => {
    if (!authUser) return;

    const collectionRef = collection(db, `users/${authUser.uid}/notifications`);

    const q = query(collectionRef, where("read", "==", false));

    const unsubscribe = onSnapshot(q, (snap) => {
      snap.docs.forEach((d) => {
        const notification = d.data();
        handleNewNotification(d.id, notification);
      });
    });

    return () => unsubscribe();
  }, [authUser]);

  const handleNewNotification = (notificationId: string, notification: any) => {
    if (notification.type === "search_alert") {
      const newNotification = {
        id: `search_improvement-${Date.now()}`,
        type: "info" as const,
        title: "New Inventory Alert",
        content: (
          <>
            <p>
              New and better matching inventory found for your past search
              query: <i>{notification.data?.query}</i>
            </p>
            <p>
              Click{" "}
              <Link to={`/${notification.data?.urlQueryString}`}>here</Link> to
              view the new results.
            </p>
          </>
        ),
      };
      addNotification(newNotification);
    } else if (notification.type === "price_alert") {
      const d = notification.data || {};
      const dropped = d.direction === "down";
      const fmt = (v: number) =>
        typeof v === "number" ? `$${v.toFixed(2)}/lb` : "";
      const newNotification = {
        id: `price_alert-${Date.now()}`,
        type: "info" as const,
        title: dropped ? "Price drop alert" : "Price change alert",
        content: (
          <>
            <p>
              The lowest price for a search you're tracking (
              <i>{d.query}</i>) {dropped ? "dropped" : "increased"}
              {d.oldPrice && d.newPrice
                ? `: ${fmt(d.oldPrice)} → ${fmt(d.newPrice)}`
                : ""}
              .
            </p>
            <p>
              Click{" "}
              <Link
                to={
                  d.urlQueryString
                    ? `/${d.urlQueryString}`
                    : `/?q=${encodeURIComponent(d.query || "")}`
                }
              >
                here
              </Link>{" "}
              to view the latest results.
            </p>
          </>
        ),
      };
      addNotification(newNotification);
    } else if (notification.type === "generic") {
      const newNotification = {
        id: `generic-${Date.now()}`,
        type: notification.data?.severity || ("info" as const),
        title: notification.data?.title || "Notification",
        content: notification.data?.message as string | ReactElement,
      };
      addNotification(newNotification);
    } else {
      console.warn("Unknown notification type:", notification.type);
    }

    // Mark notification as read in the database
    const notificationRef = doc(
      db,
      `users/${authUser?.uid}/notifications/${notificationId}`,
    );
    updateDoc(notificationRef, { read: true }).catch((error) => {
      console.error("Error marking notification as read:", error);
    });
  };

  return null; // This component does not render anything
};
